import { ReactNode } from 'react';
import { Ref } from 'react';
import { VirtuosoHandle, VirtuosoProps } from 'react-virtuoso';
import { EmptyStateConfig, PaginatedList, PaginatedListRefType } from '@frontend/components';
import { CommonHTMLAttributes, InfiniteQueryResult } from '@frontend/types';
import { InfiniteRow } from './atoms/infinite-row';
import { ColumnOutput } from './utils';

/**
 * Props for the SmallInfiniteTableView component.
 *
 * @template T - The type of data objects displayed in the table rows
 */
type SmallInfiniteTableViewProps<T extends object> = {
  /** Text or component to display when user reaches the end of the list */
  endOfListText?: React.ReactNode | string;

  /** Required infinite query result object containing data, loading states, and fetch functions */
  infiniteQueryProps: InfiniteQueryResult<T>;

  /** Optional height offset for calculations */
  offsetHeightProp?: number;

  /** Number of items to render outside the visible area for smooth scrolling (default: 10) */
  overscan?: number;

  /** Whether to enable reverse infinite scrolling (loads content at the top instead of bottom) */
  isReverseInfinite?: boolean;

  /** Maximum number of items to display per page */
  limit?: number;

  /** Fixed height for the table container */
  height?: number | string;

  /** Configuration for empty state display when no data is available */
  emptyStateConfig?: EmptyStateConfig;

  /** Custom skeleton loader component to show while data is loading */
  skeletonLoader?: React.ReactNode;

  /** Error message to display when data fetching fails */
  errorMessage?: string;

  /** Ref to access PaginatedList component methods */
  paginatedListRef?: Ref<PaginatedListRefType>;

  /** Callback fired when the user scrolls to the bottom of the list */
  atBottomStateChange?: VirtuosoProps<T, any>['atBottomStateChange'];

  /** Ref to access Virtuoso component methods for advanced scroll control */
  virtuosoRef?: Ref<VirtuosoHandle>;

  /** Index of the topmost item to display initially (for reverse infinite) */
  initialTopMostItemIndex?: number;

  /** Function to render the start column content for each row */
  startColumn?: (item: T) => ReactNode;

  /** Function to render the end column content for each row */
  endColumn?: (item: T) => ReactNode;

  /** Function to render the row header content for each row */
  rowHeader?: (item: T) => ReactNode;

  /** Required array of column configurations defining how to display each column */
  colConfig: ColumnOutput<T>[];

  /** Callback fired when a table row is clicked */
  onRowClick?: (data: T) => void;
} & CommonHTMLAttributes;
export const SmallInfiniteTableView = <T extends object>({
  endOfListText = '🎉 You have reached the end',
  infiniteQueryProps,
  skeletonLoader,
  overscan = 10,
  isReverseInfinite,
  emptyStateConfig,
  limit,
  errorMessage,
  paginatedListRef,
  atBottomStateChange,
  startColumn,
  endColumn,
  rowHeader,
  colConfig,
  onRowClick,
  ...rest
}: SmallInfiniteTableViewProps<T>) => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, status } = infiniteQueryProps;
  const isLoading = status === 'loading';
  const isError = status === 'error';

  return (
    <PaginatedList<T>
      listData={data?.pages ?? []}
      renderListItem={({ index, listItem }) => (
        <InfiniteRow
          index={index}
          data={listItem}
          startColumn={startColumn}
          endColumn={endColumn}
          rowHeader={rowHeader}
          colConfig={colConfig}
          onClick={onRowClick}
        />
      )}
      hasNextPage={hasNextPage}
      isError={isError}
      isLoading={isLoading}
      isReverseInfinite={isReverseInfinite}
      isFetchingNextPage={isFetchingNextPage}
      overscanCount={overscan}
      loadMore={fetchNextPage}
      limit={limit}
      errorMessage={errorMessage}
      emptyStateConfig={emptyStateConfig}
      endOfListText={endOfListText}
      paginatedListRef={paginatedListRef}
      atBottomStateChange={atBottomStateChange}
      {...rest}
    />
  );
};
